import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useAuditLogs } from '@/hooks/useGovernance'
import {
    Shield, Search, Filter, Loader2, Clock, User,
    ChevronDown
} from 'lucide-react'
import { format } from 'date-fns'

export function AuditPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [expandedId, setExpandedId] = useState<string | null>(null)

    useEffect(() => { setPageTitle('Logs de Auditoria') }, [setPageTitle])

    const { data: logs, isLoading } = useAuditLogs()

    const filtered = logs?.filter(log =>
        log.entidade.toLowerCase().includes(search.toLowerCase()) ||
        log.acao.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h2 className="text-xl font-bold text-surface-900">Governança & Auditoria</h2>
                <p className="text-sm text-surface-700/60 mt-0.5">Rastreabilidade completa de todas as ações no sistema</p>
            </div>

            {/* Content */}
            <div className="glass-card">
                <div className="p-4 border-b border-surface-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por entidade ou ação..."
                            className="input pl-10 h-10 text-sm" />
                    </div>
                    <button className="btn btn-sm border border-surface-200 bg-white text-surface-700 gap-2">
                        <Filter size={16} /> Filtros Avançados
                    </button>
                </div>

                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="flex justify-center p-12">
                            <Loader2 size={24} className="animate-spin text-primary-600" />
                        </div>
                    ) : filtered?.length === 0 ? (
                        <div className="text-center py-16 text-surface-700/60">
                            <Shield size={40} className="mx-auto mb-3 opacity-20" />
                            <p>Nenhum log de auditoria encontrado</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-surface-100">
                            {filtered?.map(log => (
                                <div key={log.id} className="group">
                                    <div className={`p-4 flex items-center justify-between hover:bg-surface-50 transition-colors cursor-pointer ${expandedId === log.id ? 'bg-surface-50/50' : ''}`}
                                        onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-surface-100 flex items-center justify-center text-surface-500 shrink-0">
                                                <Clock size={18} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-bold text-surface-900 uppercase">{log.acao}</span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-100 text-surface-600 font-bold uppercase tracking-wider">
                                                        {log.entidade}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4 mt-1 text-xs text-surface-700/50">
                                                    <span className="flex items-center gap-1"><User size={12} /> {log.user_id || 'Sistema'}</span>
                                                    <span>{format(new Date(log.created_at), "dd/MM/yyyy HH:mm:ss")}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronDown size={18} className={`text-surface-300 transition-transform duration-200 ${expandedId === log.id ? 'rotate-180' : ''}`} />
                                    </div>

                                    {expandedId === log.id && (
                                        <div className="px-4 pb-4 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="p-3 rounded-xl bg-surface-50 border border-surface-100">
                                                    <p className="text-[10px] font-bold text-surface-400 uppercase mb-2">Antes</p>
                                                    <pre className="text-[11px] font-mono text-surface-600 overflow-auto max-h-40">
                                                        {JSON.stringify(log.payload_antes, null, 2) || 'N/A'}
                                                    </pre>
                                                </div>
                                                <div className="p-3 rounded-xl bg-primary-50/30 border border-primary-100">
                                                    <p className="text-[10px] font-bold text-primary-400 uppercase mb-2">Depois</p>
                                                    <pre className="text-[11px] font-mono text-surface-900 overflow-auto max-h-40">
                                                        {JSON.stringify(log.payload_depois, null, 2) || 'N/A'}
                                                    </pre>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
